/**
 * The last day of a quarter, as `yyyy-MM-dd`.
 *
 * Used when a drag states a quarter but not a date: dropping a card in Q2 sets its expected
 * close to 30 June, and the server derives the quarter back from that date exactly as it does
 * for a date typed by hand.
 *
 * **Why the browser may compute this.** Quarter and year are derived values the API owns, and
 * computing one here would be a defect. This is the inverse: an *input* the client chooses,
 * from fixed calendar boundaries — 31 March, 30 June, 30 September, 31 December — with no
 * `now`, no timezone and no locale anywhere in it. Nothing about it can disagree with the
 * server, because the server still decides which quarter the date it receives belongs to.
 *
 * **Why the last day and not the first.** "Closing in Q2" is a deadline, and the quarter's end
 * is the weaker, honest reading of what a drop into that column claims. A first-day default
 * would also place the date in the *past* for most of the current quarter, which is a claim
 * about the deal that nobody made by dragging it.
 */
export function lastDayOfQuarter(quarter: number, year: number): string {
  // `Date.UTC(year, month, 0)` is the last day of the previous month, which is how each
  // quarter's end is reached without a table of lengths — and it gets February right in a
  // leap year for free.
  const endMonth = quarter * 3; // Q1 → 3, Q2 → 6, Q3 → 9, Q4 → 12
  const date = new Date(Date.UTC(year, endMonth, 0));
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${date.getUTCFullYear()}-${month}-${day}`;
}
